import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Klydo, hasSound } from "../../api/klydo";
import { isVideo, resizeCloudinary } from "../../utils/util";
import DSDialog from "../../views/system-design/DSDialog";
import KlydoModal from "../../views/Klydos/components/KlydoModal";
import { useKlydoRatingStatsBatch } from "../../views/Klydos/hooks/useKlydoRatingStats";
import DSBox from "../../views/system-design/DSBox";
import DSTypography from "../../views/system-design/DSTypography";
import DSCircularProgress from "../../views/system-design/DSCircularProgress";
import DSIconButton from "../../views/system-design/DSIconButton";
import {
  DSTable,
  DSTableBody,
  DSTableCell,
  DSTableHead,
  DSTableRow,
} from "../../views/system-design/Table";

interface KlydoTableProps {
  klydos: Klydo[];
  onRemove?: (klydoId: string) => void;
  canRemove?: boolean;
  showHeaders?: boolean;
  isLoading?: boolean;
}

const headerCellSx = {
  fontWeight: 600,
  fontSize: "11px",
  color: "text.secondary",
  padding: "8px",
};

const GifThumbnail = ({ loopUrl }: { loopUrl?: string }) => {
  if (!loopUrl || isVideo(loopUrl) === "none") {
    return (
      <DSBox
        sx={{
          width: 120,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <DSTypography>--</DSTypography>
      </DSBox>
    );
  }

  return (
    <DSBox
      sx={{
        borderRadius: "50%",
        width: 120,
        height: 120,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "center",
          position: "absolute",
          top: "0%",
          left: "0%",
          width: "100%",
          aspectRatio: "1/1",
        }}
      >
        {isVideo(loopUrl) === "image" ? (
          <img
            loading="lazy"
            alt="gif"
            src={resizeCloudinary(loopUrl, 150)}
            style={{
              width: "auto",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <video
            muted={true}
            src={resizeCloudinary(loopUrl, 150)}
            loop
            autoPlay
            style={{
              width: "auto",
              height: "100%",
              objectFit: "cover",
            }}
          />
        )}
      </DSBox>
    </DSBox>
  );
};

const KlydoTable = ({
  klydos,
  onRemove,
  canRemove = false,
  showHeaders = true,
  isLoading = false,
}: KlydoTableProps) => {
  const [showKlydoModal, setShowKlydoModal] = useState(false);
  const [selectedKlydo, setSelectedKlydo] = useState<Klydo | undefined>();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { data: ratingStatsById } = useKlydoRatingStatsBatch(
    klydos.map((k) => k.id),
  );

  const handleRemove = async (klydoId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onRemove) return;
    setDeletingId(klydoId);
    await onRemove(klydoId);
    setDeletingId(null);
  };

  const handleRowClick = (klydo: Klydo) => {
    setSelectedKlydo(klydo);
    setShowKlydoModal(true);
  };

  if (isLoading) {
    return (
      <DSBox
        sx={{ display: "flex", justifyContent: "center", padding: "20px" }}
      >
        <DSCircularProgress />
      </DSBox>
    );
  }

  return (
    <>
      <DSBox
        sx={{
          width: "100%",
          height: "100%",
          overflow: "auto",
        }}
      >
        <DSTable sx={{ width: "100%", fontSize: "13px" }}>
          {showHeaders && (
            <DSTableHead>
              <DSTableRow>
                <DSTableCell sx={{ ...headerCellSx, minWidth: "150px" }}>
                  NAME
                </DSTableCell>
                <DSTableCell sx={{ ...headerCellSx, minWidth: "100px" }}>
                  AUTHOR
                </DSTableCell>
                <DSTableCell
                  align="center"
                  sx={{ ...headerCellSx, minWidth: "50px" }}
                >
                  SOUND
                </DSTableCell>
                <DSTableCell
                  align="center"
                  sx={{ ...headerCellSx, minWidth: "50px" }}
                >
                  CHIME
                </DSTableCell>
                <DSTableCell
                  align="center"
                  sx={{ ...headerCellSx, minWidth: "70px" }}
                >
                  AI GEN
                </DSTableCell>
                <DSTableCell
                  align="center"
                  sx={{ ...headerCellSx, minWidth: "70px" }}
                >
                  AVG RATING
                </DSTableCell>
                <DSTableCell
                  align="center"
                  sx={{ ...headerCellSx, minWidth: "70px" }}
                >
                  RATING COUNT
                </DSTableCell>
                <DSTableCell
                  align="center"
                  sx={{ ...headerCellSx, minWidth: "80px" }}
                >
                  PREVIEW
                </DSTableCell>
                {canRemove && (
                  <DSTableCell
                    align="center"
                    sx={{ ...headerCellSx, width: "32px" }}
                  />
                )}
              </DSTableRow>
            </DSTableHead>
          )}
          <DSTableBody>
            {klydos.map((klydo) => (
              <DSTableRow
                key={klydo.id}
                hover
                onClick={() => handleRowClick(klydo)}
                sx={{ cursor: "pointer" }}
              >
                <DSTableCell
                  sx={{ padding: "12px 8px", fontSize: "16px" }}
                  title={klydo.id}
                >
                  {klydo.name}
                </DSTableCell>
                <DSTableCell
                  sx={{ padding: "12px 8px", fontSize: "16px" }}
                  title={klydo.creator}
                >
                  {klydo.author?.name}
                </DSTableCell>
                <DSTableCell align="center" sx={{ padding: "12px 8px" }}>
                  <DSTypography
                    sx={{
                      fontSize: "18px",
                      fontWeight: 600,
                      color: hasSound(
                        klydo.capabilitiesV2?.animationSound?.mode,
                      )
                        ? "success.main"
                        : "text.disabled",
                    }}
                  >
                    {hasSound(klydo.capabilitiesV2?.animationSound?.mode)
                      ? "✓"
                      : "✗"}
                  </DSTypography>
                </DSTableCell>
                <DSTableCell align="center" sx={{ padding: "12px 8px" }}>
                  <DSTypography
                    sx={{
                      fontSize: "18px",
                      fontWeight: 600,
                      color:
                        klydo.chimesUrls && klydo.chimesUrls.length > 0
                          ? "success.main"
                          : "text.disabled",
                    }}
                  >
                    {klydo.chimesUrls && klydo.chimesUrls.length > 0
                      ? "✓"
                      : "✗"}
                  </DSTypography>
                </DSTableCell>
                <DSTableCell align="center" sx={{ padding: "12px 8px" }}>
                  <DSTypography
                    sx={{
                      fontSize: "18px",
                      fontWeight: 600,
                      color: klydo.metadata?.generatedByAI
                        ? "info.main"
                        : "text.disabled",
                    }}
                  >
                    {klydo.metadata?.generatedByAI ? "✓" : "✗"}
                  </DSTypography>
                </DSTableCell>
                <DSTableCell align="center" sx={{ padding: "12px 8px" }}>
                  <DSTypography sx={{ fontSize: "14px", fontWeight: 600 }}>
                    {ratingStatsById?.[klydo.id]?.count
                      ? ratingStatsById[klydo.id].average.toFixed(1)
                      : "-"}
                  </DSTypography>
                </DSTableCell>
                <DSTableCell align="center" sx={{ padding: "12px 8px" }}>
                  <DSTypography sx={{ fontSize: "14px" }}>
                    {ratingStatsById?.[klydo.id]?.count ?? 0}
                  </DSTypography>
                </DSTableCell>
                <DSTableCell align="center" sx={{ padding: "12px 8px" }}>
                  <DSBox sx={{ display: "flex", justifyContent: "center" }}>
                    <GifThumbnail loopUrl={klydo.loopUrl} />
                  </DSBox>
                </DSTableCell>
                {canRemove && (
                  <DSTableCell align="center" sx={{ padding: "12px 8px" }}>
                    {deletingId === klydo.id ? (
                      <DSCircularProgress size={20} />
                    ) : (
                      <DSIconButton
                        title="Remove klydo"
                        size="small"
                        onClick={(e) => handleRemove(klydo.id, e)}
                      >
                        <CloseIcon fontSize="small" />
                      </DSIconButton>
                    )}
                  </DSTableCell>
                )}
              </DSTableRow>
            ))}
          </DSTableBody>
        </DSTable>
        {klydos.length === 0 && (
          <DSBox
            sx={{
              padding: "20px",
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            No klydos to display
          </DSBox>
        )}
      </DSBox>

      {showKlydoModal && selectedKlydo && (
        <DSDialog
          fullWidth
          maxWidth="lg"
          open={showKlydoModal}
          onClose={() => setShowKlydoModal(false)}
        >
          <KlydoModal
            klydoId={selectedKlydo.id}
            setShowModal={setShowKlydoModal}
          />
        </DSDialog>
      )}
    </>
  );
};

export default KlydoTable;
